import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useChat } from "@/components/chat/ChatWorkspaceContext";

const MAX_DRAFT = 8000;

/**
 * "Ask <agent>" from a growth page: open a fresh Chat session with a prefilled draft, the way Get Better Results does.
 * Nothing is sent; the person reads the draft and presses send. Disabled with a reason while the agent is unreachable.
 */
export function useGrowthChat() {
  const chat = useChat();
  const controller = chat?.controller;
  const navigate = useNavigate();
  const launchRef = useRef(null);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    launchRef.current = null;
    setLaunching(false);
    setError(null);
    return () => { launchRef.current = null; };
  }, [controller?.scopeKey]);

  const unavailable = !controller || controller.loading
    ? "Connecting to your agent…"
    : !controller.agent?.available ? "Chat will be ready when your agent is connected."
      : !controller.agent?.online ? "Your agent is offline right now." : "";

  const launch = useCallback(async (draft) => {
    const text = String(draft || "").trim().slice(0, MAX_DRAFT);
    if (!text || launchRef.current || unavailable) return false;
    const request = {};
    launchRef.current = request;
    setLaunching(true);
    setError(null);
    try {
      const id = await controller.createSession({ draft: text, activate: false });
      if (launchRef.current !== request) return false;
      if (!id) { setError("We couldn't open a new chat. Try again."); return false; }
      controller.setActiveId(id);
      chat.setFullscreen?.(false);
      navigate("/agent/chat");
      return true;
    } catch {
      if (launchRef.current === request) setError("We couldn't open a new chat. Try again.");
      return false;
    } finally {
      if (launchRef.current === request) { launchRef.current = null; setLaunching(false); }
    }
  }, [chat, controller, navigate, unavailable]);

  const agentName = controller?.agent?.name || null;
  return { launch, launching, error, unavailable, agentName };
}
